import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Loader2 } from 'lucide-react';
import { ErrorState } from '../../components/ui/ErrorState';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { fetchCurrentUser } from '../../features/user/userThunks';
import { ProfileCard } from './components/ProfileCard';
import { ReportingEmailsCard } from './components/ReportingEmailsCard';
import { SecurityCard } from './components/SecurityCard';
import { SoonPill } from './components/SoonPill';
import { EditProfileModal } from './modals/EditProfileModal';
import { ChangePasswordFlow } from './modals/ChangePasswordFlow';

/**
 * Mi Cuenta.
 *
 * Until now this was 131 lines of static markup: no Redux, no requests, no
 * handlers. Every account saw the same invented name, RUT, plan and join date,
 * and none of the six buttons did anything — while the backend had the whole
 * module written and exercised by the legacy front.
 *
 * **It reads what `get_user/` returns rather than assuming a profile.** That
 * payload is shaped by whichever `*ProfileSerializer` matches the account, so
 * an admin carries a RUT, a seller its company, an operator its warehouse. That
 * is what lets one page serve admin, admin_checkout, seller, driver and
 * operator. Only the admin workspace is routed today, so only admins reach it
 * for now.
 *
 * No title of its own: the header already carries it for `/account`, the same
 * as every other page in this workspace.
 */
export const Account = () => {
  const dispatch = useDispatch();
  const { data: user, isLoading, error } = useSelector((state) => state.user);
  const { user: sessionUser } = useSelector((state) => state.auth);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  const [comingSoon, setComingSoon] = useState(null);

  useEffect(() => {
    dispatch(fetchCurrentUser());
  }, [dispatch]);

  if (isLoading && !user) {
    return (
      <div className="max-w-4xl mx-auto flex items-center justify-center py-24">
        <Loader2 size={28} className="animate-spin text-ninja-green" />
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="max-w-4xl mx-auto">
        <ErrorState
          title="No pudimos cargar tu cuenta"
          description={error}
          onRetry={() => dispatch(fetchCurrentUser())}
          isRetrying={isLoading}
        />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <ProfileCard
        user={user}
        sessionUser={sessionUser}
        onEdit={() => setIsEditOpen(true)}
      />

      <ReportingEmailsCard emails={user.operative_mails} />

      <SecurityCard
        onChangePassword={() => setIsPasswordOpen(true)}
        onComingSoon={setComingSoon}
      />

      <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-8 shadow-sm border border-danger/30">
        <h3 className="text-lg font-bold text-danger mb-1 flex items-center gap-2">
          Zona Peligrosa
          <SoonPill />
        </h3>
        <p className="text-sm text-text-secondary">
          Aquí vivirán las acciones irreversibles sobre la cuenta, como darla de baja. Todavía no hay
          nada que hacer desde acá.
        </p>
      </div>

      <EditProfileModal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        user={user}
      />

      <ChangePasswordFlow
        isOpen={isPasswordOpen}
        onClose={() => setIsPasswordOpen(false)}
      />

      <Modal
        isOpen={Boolean(comingSoon)}
        onClose={() => setComingSoon(null)}
        title={comingSoon || ''}
        size="sm"
      >
        <div className="flex flex-col gap-5">
          <p className="text-sm text-text-secondary text-center">
            Todavía no está disponible. Te avisaremos cuando puedas configurarlo desde acá.
          </p>
          <div className="flex justify-end">
            <Button variant="primary" pill onClick={() => setComingSoon(null)}>
              Entendido
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
